import { v4 as uuidv4 } from "uuid";
import { create } from "zustand";
import { Plan, PlanDetail, PlanInfo } from "../../../types/plan";
import {
  callSnowflakeProxy,
  ChatMessageV5,
  ChatSuccessResponseV5,
  RelocationData,
  TraceEvent,
} from "../types/aiChatClientV5";
import {
  highlightSearchResults,
  highlightRelocationResults,
} from "../utils/aiChatUtils";
import {
  isTimeSeriesField,
  getTimeSeriesPointLabel,
  getTimeSeriesPointValue,
} from "../types/table";

export interface SpacesName {
  name: string;
  dbas: string[];
}

export interface AIChatV5Config {
  aiChatV5Endpoint?: string;
  aiChatV5Provider?: "snowflake" | "python" | "snowflake-agent" | "cadviewer-analytics";
  aiChatV5Mode?: "Test" | "Prod";
  analyticsTarget?: string;
}

type ThinkingStatus = {
  status: string;
  message: string;
};

export interface AIChatV5 {
  messages: ChatMessageV5[];
  historyMessagesToSend: ChatMessageV5[];
  isLoading: boolean;
  isSearching: boolean;
  error: string | null;
  currentSearchResult: ChatSuccessResponseV5 | null;
  currentHighlight: string | null;
  thinkingText: string | null;
  thinkingStatuses: ThinkingStatus[];
  reasoningTrace: TraceEvent[];
  clearCurrentHighlight: () => void;
  plansInfo: PlanInfo | null;
  distanceData: any | null;
  config: AIChatV5Config;

  sendMessage: (
    prompt: string,
    spacesNames: SpacesName[],
    selectedPlan?: Plan,
    selectedPlanDetail?: PlanDetail,
    useGlobalMode?: boolean,
    selectedSpaces?: string[],
    isChartMode?: boolean,
  ) => Promise<void>;
  cancelQuery: () => void;
  clearError: () => void;
  internalCADViewerAICallback: (chatMessage: ChatMessageV5) => void;
  setPlansInfoAndDistanceData: (plansInfo: any, distanceData: any) => void;
  setConfig: (config: AIChatV5Config) => void;
  clearHistoryMessagesToSend: () => void;
  addLocalMessage: (content: string, result?: any) => void;
}

export const useAIChatStoreV5 = create<AIChatV5>((set, get) => ({
  messages: [],
  historyMessagesToSend: [],
  isLoading: false,
  isSearching: false,
  error: null,
  currentSearchResult: null,
  currentHighlight: null,
  thinkingText: null,
  thinkingStatuses: [],
  reasoningTrace: [],
  plansInfo: null,
  distanceData: null,
  config: {
    aiChatV5Endpoint: "",
    aiChatV5Provider: "snowflake",
    aiChatV5Mode: "Test",
    analyticsTarget: "DEFAULT",
  },

  setPlansInfoAndDistanceData: (plansInfo, distanceData) => {
    set({ plansInfo, distanceData });
  },

  setConfig: (config) => {
    set({ config });
  },

  clearError: () => {
    set({ error: null, currentSearchResult: null });
  },

  internalCADViewerAICallback: (chatMessage: ChatMessageV5) => {
    const { currentHighlight } = get();
    if (!chatMessage.result) return;

    // Don't highlight if this is a recommendation, combination, or table response (no spaces to highlight)
    if (
      chatMessage.result.recommendations ||
      chatMessage.result.combinations ||
      chatMessage.result.tables
    ) {
      return;
    }

    // Relocation responses have their own highlighting pipeline (allowed spaces + vacants)
    if (chatMessage.result.relocation) {
      if (currentHighlight !== chatMessage.uuid) {
        highlightRelocationResults(chatMessage.result.relocation as RelocationData);
        set({ currentHighlight: chatMessage.uuid ?? null });
      } else {
        highlightSearchResults({ targetPlanInfo: undefined, searchResults: [] });
        set({ currentHighlight: null });
      }
      return;
    }

    if (currentHighlight !== chatMessage.uuid) {
      highlightSearchResults(chatMessage.result as any);
      set({ currentHighlight: chatMessage.uuid ?? null });
    } else {
      highlightSearchResults({ targetPlanInfo: undefined, searchResults: [] });
      set({ currentHighlight: null });
    }
  },

  sendMessage: async (
    prompt: string,
    _spacesNames: SpacesName[],
    selectedPlan?: Plan,
    selectedPlanDetail?: PlanDetail,
    useGlobalMode: boolean = false,
    selectedSpaces?: string[],
    isChartMode: boolean = false,
  ) => {
    if (!prompt.trim()) return;

    // Determine searchMode based on what's provided
    let searchMode: "global" | "current" | "property";
    if (useGlobalMode) {
      searchMode = "global";
    } else if (selectedPlan && selectedPlanDetail) {
      searchMode = "current";
    } else if (selectedPlan) {
      searchMode = "property";
    } else {
      searchMode = "global";
    }

    const metadata = {
      searchMode,
      selectedSpaces: selectedSpaces || [],
      planInfo:
        !useGlobalMode && selectedPlan && selectedPlanDetail
          ? {
              propertyName: selectedPlan.propertyName,
              planName: selectedPlanDetail.planName,
            }
          : !useGlobalMode && selectedPlan
            ? {
                propertyName: selectedPlan.propertyName,
              }
            : undefined,
      isChartMode,
      timestamp: new Date().toISOString(),
    };

    const newUserMessage: ChatMessageV5 = {
      uuid: uuidv4(),
      role: "user",
      content: prompt,
      metadata,
    };

    // Build conversation history from prior turns (questions + answers) so
    // follow-up questions keep the thread context. Captured BEFORE appending
    // the new message and capped to the most recent turns to bound payload.
    const conversationHistory = get()
      .messages.filter(
        (m) =>
          (m.role === "user" || m.role === "assistant") &&
          typeof m.content === "string" &&
          m.content.trim().length > 0,
      )
      .map((m) => ({ role: m.role, content: m.content }))
      .slice(-20);

    set((state) => ({
      messages: [...state.messages, newUserMessage],
      historyMessagesToSend: [...state.historyMessagesToSend, newUserMessage],
      isLoading: true,
      error: null,
      currentSearchResult: null,
      thinkingText: null,
      thinkingStatuses: [],
      reasoningTrace: [],
    }));

    try {
      const requestUuid = uuidv4();
      // Extract only planName and propertyNumber based on mode
      // For "property" mode: send only propertyNumber
      // For "current" mode: send both propertyNumber and planName
      // For "global" mode: send nothing
      const planInfo =
        !useGlobalMode && selectedPlan && selectedPlanDetail
          ? {
              planName: selectedPlanDetail.planName,
              propertyNumber: selectedPlan.propertyNumber,
              propertyName: selectedPlan.propertyName,
            }
          : !useGlobalMode && selectedPlan
            ? {
                propertyNumber: selectedPlan.propertyNumber,
                propertyName: selectedPlan.propertyName,
              }
            : undefined;

      const { config: storeConfig } = get();
      const apiUrl = storeConfig.aiChatV5Endpoint || "";

      let capturedRequestId: string | undefined;

      // Setup callbacks for stream events
      const callbacks = {
        onThinking: (text: string) => {
          set({ thinkingText: text });
        },
        onStatus: (message: string, status: string) => {
          set((state) => ({
            thinkingStatuses: [...state.thinkingStatuses, { status, message }],
          }));
        },
        onTextDelta: (text: string, contentIndex: number) => {
          // Optionally handle text deltas for streaming response
          console.log("Text delta:", text, contentIndex);
        },
        onRequestId: (requestId: string) => {
          console.log("📝 Request ID captured:", requestId);
          capturedRequestId = requestId;
        },
        onEvent: (event: string, data: any) => {
          const seq =
            typeof data?.sequence_number === "number"
              ? data.sequence_number
              : get().reasoningTrace.length;
          const { value, truncated } = capTraceData(data);
          set((state) => ({
            reasoningTrace: [
              ...state.reasoningTrace,
              { event, seq, data: value, ...(truncated ? { truncated } : {}) },
            ],
          }));
        },
      };

      const result = await callSnowflakeProxy(
        apiUrl,
        prompt,
        planInfo,
        storeConfig.aiChatV5Provider,
        storeConfig.aiChatV5Mode,
        storeConfig.aiChatV5Provider === "snowflake-agent"
          ? callbacks
          : undefined,
        selectedSpaces,
        isChartMode,
        storeConfig.analyticsTarget,
        conversationHistory,
      );

      // Normalize to UI result if we have space-level data, otherwise no highlight
      const normalized = normalizeProxyResponseToUI(result);

      const resultAny = result as any;
      const extractedQueryLogId: string | undefined =
        resultAny?.data?.queryLogId ??
        resultAny?.queryLogId ??
        resultAny?.data?.query_log_id ??
        resultAny?.query_log_id ??
        resultAny?.data?.response?.queryLogId ??
        resultAny?.data?.response?.query_log_id;
      console.log("📝 queryLogId extracted:", extractedQueryLogId, "from result:", resultAny);

      // Capture the reasoning streamed during this query (snowflake-agent only)
      // before the live thinking state is reset below, so it can be replayed
      // from a button on the response.
      const {
        thinkingText: capturedThinkingText,
        thinkingStatuses: capturedThinkingStatuses,
        reasoningTrace: capturedReasoningTrace,
      } = get();

      const assistantMessage: ChatMessageV5 = {
        uuid: requestUuid,
        role: "assistant",
        content: normalized.message,
        rawContent: JSON.stringify(result, null, 2),
        result: normalized.highlightResult || undefined,
        requestId: capturedRequestId,
        queryLogId: extractedQueryLogId,
        ...(capturedThinkingText ? { thinkingText: capturedThinkingText } : {}),
        ...(capturedThinkingStatuses.length > 0
          ? { thinkingStatuses: capturedThinkingStatuses }
          : {}),
        ...(capturedReasoningTrace.length > 0
          ? { reasoningTrace: capturedReasoningTrace }
          : {}),
      };

      set((state) => ({
        messages: [...state.messages, assistantMessage],
        isLoading: false,
        isSearching: false,
        currentSearchResult: result,
        thinkingText: null,
        thinkingStatuses: [],
        reasoningTrace: [],
      }));

      // Only highlight if we have search results (not recommendations, combinations, or tables)
      if (
        normalized.highlightResult &&
        !normalized.highlightResult.recommendations &&
        !normalized.highlightResult.combinations &&
        !normalized.highlightResult.tables
      ) {
        setTimeout(() => {
          get().internalCADViewerAICallback(assistantMessage);
        }, 200);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unknown error occurred.";
      const errorAssistantMessage: ChatMessageV5 = {
        uuid: uuidv4(),
        role: "assistant",
        content: `Sorry, I encountered an error: ${errorMessage}`,
      };
      set((state) => ({
        messages: [...state.messages, errorAssistantMessage],
        isLoading: false,
        isSearching: false,
        error: errorMessage,
        thinkingText: null,
        thinkingStatuses: [],
        reasoningTrace: [],
      }));
    }
  },

  clearHistoryMessagesToSend: () => {
    set({ historyMessagesToSend: [], messages: [] });
  },

  addLocalMessage: (content: string, result?: any) => {
    const newMessage: ChatMessageV5 = {
      uuid: uuidv4(),
      role: "assistant",
      content,
      result,
      metadata: {
        searchMode: "current",
        timestamp: new Date().toISOString(),
      },
    };

    set((state) => ({
      messages: [...state.messages, newMessage],
    }));
  },

  clearCurrentHighlight: () => {
    set({ currentHighlight: null });
  },

  cancelQuery: () => {
    set({
      isLoading: false,
      isSearching: false,
      thinkingText: null,
      thinkingStatuses: [],
      reasoningTrace: [],
      error: "Query cancelled by user",
    });
  },
}));

// Helpers

// Bound the size of a persisted trace event: large arrays (e.g. SQL result
// rows) are capped and long strings truncated so accumulating traces across a
// session stays in-memory-friendly. Returns whether any trimming happened.
const TRACE_MAX_ARRAY = 200;
const TRACE_MAX_STRING = 5000;
function capTraceData(value: any): { value: any; truncated: boolean } {
  let truncated = false;
  const walk = (v: any, depth: number): any => {
    if (depth > 8) return v;
    if (typeof v === "string") {
      if (v.length > TRACE_MAX_STRING) {
        truncated = true;
        return v.slice(0, TRACE_MAX_STRING) + ` … (+${v.length - TRACE_MAX_STRING} chars)`;
      }
      return v;
    }
    if (Array.isArray(v)) {
      const capped = v.length > TRACE_MAX_ARRAY ? v.slice(0, TRACE_MAX_ARRAY) : v;
      if (v.length > TRACE_MAX_ARRAY) truncated = true;
      return capped.map((item) => walk(item, depth + 1));
    }
    if (v && typeof v === "object") {
      const out: Record<string, any> = {};
      for (const key of Object.keys(v)) out[key] = walk(v[key], depth + 1);
      return out;
    }
    return v;
  };
  return { value: walk(value, 0), truncated };
}

type UIHighlightResult = {
  targetPlanInfo?: {
    spaceNumber?: string;
    spaceHeader?: string;
    summary?: { gla?: string };
    detailTabs: any[];
    highlight_color?: string;
  };
  targetPlanInfos?: Array<{
    spaceNumber?: string;
    spaceHeader?: string;
    summary?: { gla?: string };
    detailTabs: any[];
    highlight_color?: string;
  }>; // Array of targets when query_interpretation is an array
  searchResults?: Array<{
    spaceID?: string;
    area?: number | string;
    tenant?: string;
    distance?: number;
    totalRentPSF?: string;
    leaseEndDate?: string;
    totalRent?: string;
    highlight_color?: string;
  }>;
  recommendations?: any[]; // Recommendations from tenant_recommendation response
  target_space?: any; // Target space for recommendation responses (exposed at root level)
  combinations?: any[]; // Space combinations from combination response
  tables?: {
    labels: Record<string, string>;
    data: Record<string, any>[];
  }; // Table data from table response
  visualizations?: Array<{
    title: string;
    type: "bar" | "line" | "doughnut" | "pie" | "radar";
    data: {
      labels: string[];
      datasets: Array<{
        label?: string;
        data: number[];
        backgroundColor?: string | string[];
        borderColor?: string | string[];
        borderWidth?: number;
        tension?: number;
        fill?: boolean;
      }>;
    };
    options?: any;
  }>; // Visualizations from response
  query_interpretation?: any; // Query interpretation metadata
  relocation?: RelocationData; // Hybrid doc+sql relocation response
};

const TIME_SERIES_PALETTE = [
  "#36A2EB",
  "#4BC0C0",
  "#FFCE56",
  "#FF6384",
  "#9966FF",
  "#FF9F40",
  "#2ECC71",
  "#E67E22",
];

/**
 * Inspect a table response for any time-series columns and synthesise a
 * line chart that concatenates every point across rows. Returns an empty
 * array when no usable series is detected.
 */
function buildTimeSeriesVisualizations(tables: {
  labels: Record<string, string>;
  data: Record<string, any>[];
}): Array<{
  title: string;
  type: "line";
  data: { labels: string[]; datasets: any[] };
  options?: any;
}> {
  if (!tables?.data?.length) return [];

  const seriesFields = Object.keys(tables.labels || {}).filter((f) =>
    isTimeSeriesField(f)
  );
  if (seriesFields.length === 0) return [];

  const visualizations: Array<{
    title: string;
    type: "line";
    data: { labels: string[]; datasets: any[] };
    options?: any;
  }> = [];

  for (const fieldName of seriesFields) {
    const rowsWithSeries = tables.data.filter(
      (row) => Array.isArray(row?.[fieldName]) && row[fieldName].length > 0
    );
    if (rowsWithSeries.length === 0) continue;

    // Concatenate all points across rows preserving order.
    const labels: string[] = [];
    const values: number[] = [];
    for (const row of rowsWithSeries) {
      for (const point of row[fieldName] as any[]) {
        if (!point || typeof point !== "object") continue;
        labels.push(getTimeSeriesPointLabel(point));
        values.push(getTimeSeriesPointValue(point));
      }
    }
    if (values.length === 0) continue;

    const datasetLabel = tables.labels[fieldName] || fieldName;
    const color = TIME_SERIES_PALETTE[visualizations.length % TIME_SERIES_PALETTE.length];

    visualizations.push({
      title: `${datasetLabel} — evolution`,
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: datasetLabel,
            data: values,
            borderColor: color,
            backgroundColor: color + "22",
            borderWidth: 2,
            fill: true,
            tension: 0.35,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: { position: "top" },
          title: { display: false },
        },
        scales: {
          y: {
            beginAtZero: false,
            title: { display: true, text: datasetLabel },
          },
        },
      },
    });
  }

  return visualizations;
}

function normalizeProxyResponseToUI(result: ChatSuccessResponseV5): {
  message: string;
  highlightResult?: UIHighlightResult;
} {
  // Support both nested (data.response.X) and flat (data.X) response structures
  const responseData = result?.data?.response ?? result?.data;
  const message = responseData?.message || "";

  // Hybrid doc+sql relocation response — detect by format_type or by shape
  const formatType = (responseData as any)?.format_type;
  const relocationResults = (responseData as any)?.results;
  const isRelocation =
    formatType === "hybrid_doc_sql.relocation" ||
    (Array.isArray(relocationResults) &&
      relocationResults.some(
        (r: any) =>
          r &&
          (Array.isArray(r.allowed_space_numbers) ||
            Array.isArray(r.allowed_spaces_details))
      ));

  if (isRelocation) {
    const rawTargetSpace = (responseData as any)?.target_space;
    const normalizedTargetSpace =
      typeof rawTargetSpace === "string"
        ? rawTargetSpace
        : rawTargetSpace?.space_number
          ? String(rawTargetSpace.space_number)
          : undefined;

    const relocation: RelocationData = {
      format_type: "hybrid_doc_sql.relocation",
      message,
      query_interpretation: (responseData as any)?.query_interpretation,
      target_space: normalizedTargetSpace,
      results: (Array.isArray(relocationResults) ? relocationResults : []).map(
        (r: any) => ({
          tenant_name: r.tenant_name,
          store_record_key: r.store_record_key,
          store_table_name: r.store_table_name,
          store_mall_name: r.store_mall_name,
          has_relocation_clause: r.has_relocation_clause,
          section_restriction: r.section_restriction,
          allowed_space_numbers: Array.isArray(r.allowed_space_numbers)
            ? r.allowed_space_numbers.map((s: any) => String(s))
            : [],
          allowed_spaces_details: Array.isArray(r.allowed_spaces_details)
            ? r.allowed_spaces_details.map((s: any) => ({
                space_number: String(s.space_number),
                tenant_name: s.tenant_name,
                mall_name: s.mall_name,
                gla: s.gla,
                lease_end_date: s.lease_end_date,
                lease_status: s.lease_status,
                total_rent: s.total_rent,
                sales_r12: s.sales_r12,
                area_unit: s.area_unit,
                currency: s.currency,
                ...(s.highlight_color && { highlight_color: s.highlight_color }),
              }))
            : [],
          supporting_quote: r.supporting_quote,
          source_document_id: r.source_document_id,
        })
      ),
      analysis_summary: (responseData as any)?.analysis_summary,
    };

    return {
      message,
      highlightResult: { relocation },
    };
  }

  // Check if this is a table type response
  const tables = (responseData as any)?.tables;
  const visualizations = (responseData as any)?.visualizations;

  if (tables && tables.labels && tables.data && Array.isArray(tables.data)) {
    // This is a table response (may also include visualizations)
    const baseVisualizations =
      visualizations && Array.isArray(visualizations) ? [...visualizations] : [];

    const derived = buildTimeSeriesVisualizations(tables);
    const allVisualizations = [...baseVisualizations, ...derived];

    return {
      message,
      highlightResult: {
        tables: {
          labels: tables.labels,
          data: tables.data,
        },
        visualizations: allVisualizations.length > 0 ? allVisualizations : undefined,
        query_interpretation: (responseData as any)
          ?.query_interpretation,
      },
    };
  }

  // Check if this is a combination type response
  const combinations = (responseData as any)?.combinations;

  if (combinations && Array.isArray(combinations) && combinations.length > 0) {
    // This is a combination response. Payloads can ship fields under
    // either the legacy names (total_area_sf, total_rent_usd, area_sf, ...)
    // or the newer simpler names (total_area, total_rent, area, ...) with
    // explicit area_unit / currency. Pass both through so the UI can fall
    // back regardless of which variant arrived.
    return {
      message,
      highlightResult: {
        combinations: combinations.map((combo: any) => ({
          rank: combo.rank,
          combination_id: combo.combination_id,
          total_area_sf: combo.total_area_sf,
          total_area: combo.total_area,
          total_rent_usd: combo.total_rent_usd,
          total_rent: combo.total_rent,
          area_unit: combo.area_unit,
          currency: combo.currency,
          earliest_expiration: combo.earliest_expiration,
          latest_expiration: combo.latest_expiration,
          days_until_earliest_expiration: combo.days_until_earliest_expiration,
          total_rent_remaining_usd: combo.total_rent_remaining_usd,
          buyout_options: combo.buyout_options,
          buyout_cost_usd: combo.buyout_cost_usd,
          spaces: (combo.spaces || []).map((space: any) => ({
            space_number: space.space_number,
            tenant_name: space.tenant_name,
            area_sf: space.area_sf,
            area: space.area,
            area_unit: space.area_unit,
            total_rent_usd: space.total_rent_usd,
            total_rent: space.total_rent,
            currency: space.currency,
            rent_per_sf: space.rent_per_sf,
            sales_r12_usd: space.sales_r12_usd,
            occupancy_percentage: space.occupancy_percentage,
            lease_start_date: space.lease_start_date,
            lease_end_date: space.lease_end_date,
            days_until_expiration: space.days_until_expiration,
            rent_remaining_usd: space.rent_remaining_usd,
            kickout_option: space.kickout_option,
            deal_type: space.deal_type,
            deal_stage: space.deal_stage,
            lease_status: space.lease_status,
            merchandise_category: space.merchandise_category,
            mall_name: space.mall_name,
            plan_name: space.plan_name,
            property_number: space.property_number,
            ...(space.highlight_color && { highlight_color: space.highlight_color }),
          })),
          scoring_breakdown: combo.scoring_breakdown,
          analysis: combo.analysis,
        })),
      },
    };
  }

  // Check if this is a recommendation type response
  const recommendations = (responseData as any)?.recommendations;

  if (
    recommendations &&
    Array.isArray(recommendations) &&
    recommendations.length > 0
  ) {
    // This is a recommendation response with factual data only
    const targetSpace = (responseData as any)?.target_space ?? {};
    return {
      message,
      highlightResult: {
        recommendations: recommendations.map((rec: any) => ({
          tenant_name: rec.tenant_name,
          financial_metrics: rec.financial_metrics,
          target_space: targetSpace,
          co_tenant_alignment: rec.co_tenant_alignment,
          success_pattern: rec.success_pattern,
        })),
        // Expose target_space at root level for useEffect and JSX access
        target_space: targetSpace,
        // Propagate highlight_color from target_space to targetPlanInfo for recommendations
        ...(targetSpace.space_number && {
          targetPlanInfo: {
            spaceNumber: targetSpace.space_number,
            spaceHeader: targetSpace.current_tenant || undefined,
            summary: { gla: targetSpace.area_sf?.toString() || "" },
            detailTabs: [],
            ...(targetSpace.highlight_color && { highlight_color: targetSpace.highlight_color }),
          },
        }),
      },
    };
  }

  // Try to extract matches compatible with highlighting (if any)
  const exact = (responseData as any)?.exact_matches || [];
  const partial = (responseData as any)?.partial_matches || [];
  const matches = [...exact, ...partial];

  // If there are items with space_number/tenant_name, preserve all fields and normalize key ones
  const searchResults = matches
    // .filter((m: any) => m?.space_number || m?.spaceID)
    .map((m: any) => {
      // Keep all original fields from the match
      const rawData = { ...m };

      // Normalize key fields while preserving originals
      return {
        ...rawData,
        // Normalized fields for compatibility
        spaceID: m.space_number || m.spaceID,
        tenant: m.tenant_name || m.tenant,
        area: m.area_sf || m.area_m2 || m.area,
        distance: m.distance_ft || m.distance_m || 0,
        totalRentPSF:
          (m.rent_per_sf || m.rent_per_m2 || "")?.toString?.() || "",
        leaseEndDate: m.lease_end_date || "",
        totalRent: (m.total_rent || m.total_rent_usd || "")?.toString?.() || "",
        totalSales: (m.sales_r12 || m.sales_r12_usd || "")?.toString?.() || "",
        totalSalesPSF: (m.sales_per_sf || "")?.toString?.() || "",
        mall_name: m.mall_name,
        property_name: m.property_name,
        plan_name: m.plan_name,
      };
    });

  const hasHighlightable = searchResults.length > 0;

  if (!hasHighlightable) {
    return { message };
  }

  const target = (responseData as any)?.query_interpretation;
  let targetPlanInfo: UIHighlightResult["targetPlanInfo"] | undefined =
    undefined;
  let targetPlanInfos: UIHighlightResult["targetPlanInfos"] | undefined =
    undefined;

  if (target) {
    if (Array.isArray(target) && target.length > 0) {
      // Handle array case (multiple targets) - create array of all targets
      targetPlanInfos = target
        .map((targetObj: any) => {
          const spaceNumber =
            targetObj.reference_space || targetObj.space_number;
          const spaceHeader =
            targetObj.tenant_name || targetObj.space_header || undefined;

          // Only include if we have at least spaceNumber or spaceHeader
          if (spaceNumber || spaceHeader) {
            return {
              spaceNumber,
              spaceHeader,
              summary: { gla: targetObj.target_area_sf || "" },
              detailTabs: [],
            };
          }
          return null;
        })
        .filter(
          (info: any) => info !== null,
        ) as UIHighlightResult["targetPlanInfos"];

      // Also set the first one as targetPlanInfo for backward compatibility
      if (targetPlanInfos && targetPlanInfos.length > 0) {
        targetPlanInfo = targetPlanInfos[0];
      }
    } else if (typeof target === "object" && !Array.isArray(target)) {
      // Handle single object case
      const spaceNumber = target.reference_space || target.space_number;
      const spaceHeader =
        target.tenant_name || target.space_header || undefined;

      // Only create targetPlanInfo if we have at least spaceNumber or spaceHeader
      if (spaceNumber || spaceHeader) {
        targetPlanInfo = {
          spaceNumber,
          spaceHeader,
          summary: { gla: target.target_area_sf || "" },
          detailTabs: [],
        };
      }
    }
  }

  return {
    message,
    highlightResult: {
      targetPlanInfo,
      targetPlanInfos,
      searchResults,
    },
  };
}
